import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { SelectChildren } from '../../../../components/TabBindingStudAccount/AddStudAccountModal'
import classes from './index.module.css'
import cn from 'classnames'
import Input from '@pages/Admin/CRM/components/base/Input/Input'
import { CheckBoxLabel } from '@UI/CheckBox'
import { getLeadFieldValue } from '@pages/Admin/CRM/utils/leadFields'
import type { LeadProfileLead } from '../../../../LeadProfileModal.types'
import InfoComp from '@pages/Admin/CRMSettings/components/InfoComp/InfoComp'
import { useAddAlert } from '@/hooks/useAddAlert'
import { childrenAPI, crmApi } from '@/api'
import { isError } from '@/helpers/checkQuery'
import { customAlphabet } from 'nanoid'

const generatePassword = customAlphabet('ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789', 8)

interface CreateOrBindingStudentContentProps {
  type: 'CREATE' | 'BIND'
  lead: LeadProfileLead
}

export interface CreateOrBindingStudentContentHandle {
  handleSave: () => Promise<number | 'error'>
}

const CreateOrBindingStudentContent = forwardRef<
  CreateOrBindingStudentContentHandle,
  CreateOrBindingStudentContentProps
>(({ type, lead }, ref) => {
  const addAlert = useAddAlert()

  // для Binding существующего студента
  const [userId, setUserId] = useState<number | null>(null)

  // для создания нового студента
  const [newUserData, setNewUserData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',

    isCreatePassword: false,
    isSendEmail: true,
  })

  const handlerUpdateUserData = <K extends keyof typeof newUserData>(
    type: K,
    value: (typeof newUserData)[K],
  ) => {
    setNewUserData((prev) => ({
      ...prev,
      [type]: value,
    }))
  }

  useEffect(() => {
    setNewUserData((prev) => ({
      ...prev,
      name: getLeadFieldValue(lead, 'NAME') || '',
      email: getLeadFieldValue(lead, 'EMAIL') || '',
      phone: getLeadFieldValue(lead, 'PHONE') || '',
    }))
  }, [lead])

  //  -------- SAVE ---------

  const handleSaveBinding = async (): Promise<number | 'error'> => {
    if (!userId) {
      addAlert({
        title: 'Ошибка',
        text: 'Не выбран личный кабинет студента',
        type: 'error',
      })
      return 'error'
    }

    const resCheckUser = await crmApi.getLeadsByUserId({
      user_id: userId,
    })

    if (isError(resCheckUser)) {
      addAlert({
        title: 'Ошибка',
        text: 'Ошибка проверки, есть ли у студента привязанный лид',
        type: 'error',
      })
      return 'error'
    }

    if (resCheckUser.data.data.length > 0) {
      addAlert({
        title: 'Ошибка',
        text: `Данный пользователь уже привязан к лиду, id лида: ${resCheckUser.data.data
          .map((l) => l.id)
          .join(', ')}`,
        type: 'error',
      })
      return 'error'
    }

    try {
      const res = await crmApi.bindingStudAccount({
        lead_id: lead.id,
        user_id: userId,
      })

      if (isError(res)) throw new Error()
    } catch (error) {
      addAlert({
        title: 'Ошибка',
        text: 'Ошибка привязки личного кабинета студента',
        type: 'error',
      })
      return 'error'
    }

    return userId
  }

  const handleSaveCreate = async (): Promise<number | 'error'> => {
    let userId: number | null = null
    if (newUserData.isCreatePassword && newUserData.password.trim()?.length > 0 && newUserData.password.trim()?.length < 5) {
      addAlert({
        title: 'Ошибка',
        text: 'Пароль должен быть не менее 5 символов, или вы можете оставить это поле пустым, тогда пароль сгенерируется автоматически',
        type: 'error',
      })
      return 'error'
    }

    try {
      const formData = new FormData()
      formData.append('name', newUserData.name)
      formData.append('email', newUserData.email)
      formData.append('phone', newUserData.phone)
      formData.append('password', newUserData.isCreatePassword ? newUserData.password.trim() || generatePassword() : '')
      formData.append('send_email', newUserData.isSendEmail.toString())

      const res = await childrenAPI.add(formData)

      if (isError(res)) throw new Error()
      userId = res.data?.data?.id
    } catch (error) {
      addAlert({
        title: 'Ошибка',
        text: 'Ошибка создания личного кабинета студента',
        type: 'error',
      })
      return 'error'
    }

    if (!userId) {
      addAlert({
        title: 'Ошибка',
        text: 'Не удалось получить ID созданного студента',
        type: 'error',
      })
      return 'error'
    }

    try {
      const res = await crmApi.bindingStudAccount({
        lead_id: lead.id,
        user_id: userId,
      })

      if (isError(res)) throw new Error()
    } catch (error) {
      addAlert({
        title: 'Ошибка',
        text: 'Ошибка привязки личного кабинета студента',
        type: 'error',
      })
      return 'error'
    }

    return userId
  }

  useImperativeHandle(ref, () => ({
    handleSave: type === 'CREATE' ? async () => await handleSaveCreate() : handleSaveBinding,
  }))

  return (
    <>
      {type === 'CREATE' ? (
        <>
          <div className={classes.inputWithLabel}>
            <div className={classes.inputLabel}>ФИО учащегося</div>
            <Input
              placeholder="Фамилия Имя Отчество"
              value={newUserData.name}
              onChange={(e) => handlerUpdateUserData('name', e.target.value)}
            />
          </div>
          <div className={classes.inputWithLabel}>
            <div className={classes.inputLabel}>Введите e-mail</div>
            <Input
              placeholder="E-mail"
              value={newUserData.email}
              onChange={(e) => handlerUpdateUserData('email', e.target.value)}
            />
          </div>
          <div className={classes.inputWithLabel}>
            <div className={classes.inputLabel}>Номер телефона учащегося</div>
            <Input
              placeholder="+7 (999) 999-99-99"
              value={newUserData.phone}
              onChange={(e) => handlerUpdateUserData('phone', e.target.value)}
            />
          </div>
          <CheckBoxLabel
            value={newUserData.isCreatePassword}
            onClick={() =>
              handlerUpdateUserData(
                'isCreatePassword',
                !newUserData.isCreatePassword,
              )
            }
            label="Создать пароль вручную"
            className={classes.checkboxWithLabel}
          />
          {newUserData.isCreatePassword && (
            <div className={classes.inputWithLabel}>
              <div className={classes.inputLabel}>Пароль</div>
              <div className={classes.inputWithInfoContainer}>
                <Input
                  placeholder="Создайте пароль"
                  value={newUserData.password}
                  onChange={(e) =>
                    handlerUpdateUserData('password', e.target.value)
                  }
                />
                <InfoComp
                  className={classes.infoInInput}
                  text="Если это поле оставить пустым, то пароль сгенерируется самостоятельно"
                />
              </div>
            </div>
          )}
          <CheckBoxLabel
            value={newUserData.isSendEmail}
            onClick={() =>
              handlerUpdateUserData('isSendEmail', !newUserData.isSendEmail)
            }
            label="Отправить приветственный e-mail (с паролем)"
            className={classes.checkboxWithLabel}
          />
        </>
      ) : (
        <>
          <div className={classes.inputWithLabel}>
            <div className={classes.inputLabel}>Личный кабинет</div>
            <SelectChildren
              value={userId}
              onChange={setUserId}
              className={classes.customInput}
            />
          </div>
        </>
      )}
    </>
  )
})

export default CreateOrBindingStudentContent
